import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOrgProject } from '../contexts/OrgProjectContext';
import { apiFetch } from '../apiClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ─── Design Tokens (matching project conventions) ─────────────────────────────
const C = {
  navy:       '#0F172A',
  navyMid:    '#1E293B',
  navyLight:  '#334155',
  blue:       '#3B82F6',
  blueLt:     '#DBEAFE',
  green:      '#10B981',
  violet:     '#8B5CF6',
  white:      '#FFFFFF',
  slate:      '#64748B',
  slateLight: '#94A3B8',
  border:     '#E2E8F0',
  bg:         '#F8FAFC',
  text:       '#1E293B',
  textMid:    '#475569',
};

/**
 * Truncates a string to maxLen characters, appending ellipsis if truncated.
 */
function truncate(str, maxLen = 30) {
  if (!str) return '';
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen) + '…';
}

/**
 * ContextSwitcher — top-nav org/project selector dropdown.
 *
 * Shows the currently selected Organisation and Project names (truncated at 30 chars).
 * On click, displays a hierarchical dropdown listing all orgs with expandable projects.
 * Includes a text filter when more than 10 orgs are available.
 * On selection change, updates the OrgProjectContext (which handles localStorage persistence).
 */
export default function ContextSwitcher() {
  const { orgId, orgName, projectId, projectName, setOrg, setProject, loading } = useOrgProject();

  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [projectsByOrg, setProjectsByOrg] = useState({}); // { orgId: [projects] }
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [filter, setFilter] = useState('');
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Fetch orgs when dropdown opens
  const fetchOrgs = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const res = await apiFetch(`/api/orgs?per_page=200`);
      if (res.ok) {
        const data = await res.json();
        const orgList = Array.isArray(data) ? data : (data.data || data.results || data.items || []);
        setOrgs(orgList);
      }
    } catch {
      // silently fail — user can retry by reopening
    } finally {
      setLoadingOrgs(false);
    }
  }, []);

  // Fetch projects for a specific org
  const fetchProjects = useCallback(async (forOrgId) => {
    if (projectsByOrg[forOrgId]) return; // already cached
    try {
      const res = await apiFetch(`/api/orgs/${forOrgId}/projects`);
      if (res.ok) {
        const data = await res.json();
        const projList = Array.isArray(data) ? data : (data.data || data.results || data.items || []);
        setProjectsByOrg(prev => ({ ...prev, [forOrgId]: projList }));
      }
    } catch {
      // silently fail
    }
  }, [projectsByOrg]);

  const handleToggle = () => {
    if (!open) {
      fetchOrgs();
      setFilter('');
      // Expand the currently selected org by default
      if (orgId) {
        setExpandedOrg(orgId);
        fetchProjects(orgId);
      }
    }
    setOpen(prev => !prev);
  };

  const handleExpandOrg = (org) => {
    const newExpanded = expandedOrg === org.id ? null : org.id;
    setExpandedOrg(newExpanded);
    if (newExpanded) {
      fetchProjects(org.id);
    }
  };

  const handleSelectProject = (org, project) => {
    setOrg(org.id, org.name, org.slug);
    setProject(project.id, project.name, project.slug);
    setOpen(false);
  };

  const handleSelectOrgOnly = (org) => {
    // Select org and its first project (if any)
    setOrg(org.id, org.name, org.slug);
    const projects = projectsByOrg[org.id];
    if (projects && projects.length > 0) {
      setProject(projects[0].id, projects[0].name, projects[0].slug);
    }
    setOpen(false);
  };

  // Filter orgs by name
  const filteredOrgs = filter
    ? orgs.filter(o => o.name.toLowerCase().includes(filter.toLowerCase()))
    : orgs;

  const showFilter = orgs.length > 10;

  if (loading) {
    return (
      <div style={styles.trigger}>
        <span style={styles.loadingText}>Loading…</span>
      </div>
    );
  }

  return (
    <div ref={dropdownRef} style={styles.container}>
      {/* Trigger button */}
      <button
        onClick={handleToggle}
        style={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Current context: ${orgName || 'No org'} / ${projectName || 'All Projects'}`}
      >
        <span style={styles.orgLabel} title={orgName || 'No Organisation'}>{truncate(orgName || 'No Organisation', 30)}</span>
        <span style={styles.separator}>/</span>
        <span style={styles.projectLabel} title={projectName || 'All Projects'}>{truncate(projectName || 'All Projects', 30)}</span>
        <span style={styles.chevron}>{open ? '▲' : '▼'}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={styles.dropdown} role="listbox">
          {/* Filter input (shown when > 10 orgs) */}
          {showFilter && (
            <div style={styles.filterContainer}>
              <input
                type="text"
                placeholder="Filter organisations…"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                style={styles.filterInput}
                autoFocus
                aria-label="Filter organisations"
              />
            </div>
          )}

          {/* Org list */}
          <div style={styles.orgList}>
            {loadingOrgs && (
              <div style={styles.loadingRow}>Loading organisations…</div>
            )}
            {!loadingOrgs && filteredOrgs.length === 0 && (
              <div style={styles.emptyRow}>
                {filter ? 'No matching organisations' : 'No organisations available'}
              </div>
            )}
            {filteredOrgs.map(org => {
              const isExpanded = expandedOrg === org.id;
              const isCurrentOrg = org.id === orgId;
              const projects = projectsByOrg[org.id] || [];

              return (
                <div key={org.id} style={styles.orgItem}>
                  {/* Org row */}
                  <div
                    style={{
                      ...styles.orgRow,
                      background: isCurrentOrg ? `${C.blue}12` : 'transparent',
                    }}
                  >
                    <button
                      onClick={() => handleExpandOrg(org)}
                      style={styles.expandBtn}
                      aria-label={isExpanded ? `Collapse ${org.name}` : `Expand ${org.name}`}
                    >
                      <span style={{
                        ...styles.expandIcon,
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      }}>▶</span>
                    </button>
                    <span
                      style={{
                        ...styles.orgName,
                        fontWeight: isCurrentOrg ? 700 : 500,
                        color: isCurrentOrg ? C.blue : C.text,
                      }}
                      onClick={() => handleExpandOrg(org)}
                      title={org.name}
                    >
                      {truncate(org.name, 30)}
                    </span>
                    {isCurrentOrg && <span style={styles.currentBadge}>current</span>}
                  </div>

                  {/* Projects list (expanded) */}
                  {isExpanded && (
                    <div style={styles.projectList}>
                      {projects.length === 0 && (
                        <div style={styles.projectLoading}>Loading projects…</div>
                      )}
                      {/* "All Projects" option for org-level aggregate view */}
                      {projects.length > 0 && (
                        <button
                          onClick={() => {
                            setOrg(org.id, org.name, org.slug);
                            setProject(null, null, null);
                            setOpen(false);
                          }}
                          style={{
                            ...styles.projectRow,
                            background: (!projectId && org.id === orgId) ? `${C.violet}18` : 'transparent',
                            fontWeight: (!projectId && org.id === orgId) ? 700 : 400,
                            color: (!projectId && org.id === orgId) ? C.violet : C.textMid,
                            fontStyle: 'italic',
                          }}
                          role="option"
                          aria-selected={!projectId && org.id === orgId}
                          title="View aggregated stats across all projects"
                        >
                          <span style={styles.projectIcon}>🏢</span>
                          <span>All Projects</span>
                          {(!projectId && org.id === orgId) && <span style={styles.checkmark}>✓</span>}
                        </button>
                      )}
                      {projects.map(proj => {
                        const isCurrentProject = proj.id === projectId && org.id === orgId;
                        return (
                          <button
                            key={proj.id}
                            onClick={() => handleSelectProject(org, proj)}
                            style={{
                              ...styles.projectRow,
                              background: isCurrentProject ? `${C.blue}18` : 'transparent',
                              fontWeight: isCurrentProject ? 700 : 400,
                              color: isCurrentProject ? C.blue : C.text,
                            }}
                            role="option"
                            aria-selected={isCurrentProject}
                            title={proj.name}
                          >
                            <span style={styles.projectIcon}>📁</span>
                            <span>{truncate(proj.name, 30)}</span>
                            {isCurrentProject && <span style={styles.checkmark}>✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  container: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 12px',
    borderRadius: 6,
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.08)',
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontSize: 13,
    color: C.white,
    whiteSpace: 'nowrap',
  },
  orgLabel: {
    fontWeight: 600,
    color: C.white,
    fontSize: 13,
  },
  separator: {
    color: C.slateLight,
    fontSize: 12,
    margin: '0 2px',
  },
  projectLabel: {
    fontWeight: 400,
    color: C.slateLight,
    fontSize: 13,
  },
  chevron: {
    fontSize: 9,
    color: C.slateLight,
    marginLeft: 4,
  },
  loadingText: {
    color: C.slateLight,
    fontSize: 12,
    fontStyle: 'italic',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 6,
    minWidth: 280,
    maxWidth: 360,
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)',
    zIndex: 10000,
    overflow: 'hidden',
  },
  filterContainer: {
    padding: '10px 12px 6px',
    borderBottom: `1px solid ${C.border}`,
  },
  filterInput: {
    width: '100%',
    padding: '7px 10px',
    fontSize: 12,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    outline: 'none',
    color: C.text,
    background: C.bg,
  },
  orgList: {
    maxHeight: 320,
    overflowY: 'auto',
    padding: '6px 0',
  },
  loadingRow: {
    padding: '12px 16px',
    fontSize: 12,
    color: C.slate,
    fontStyle: 'italic',
  },
  emptyRow: {
    padding: '12px 16px',
    fontSize: 12,
    color: C.slate,
    fontStyle: 'italic',
  },
  orgItem: {
    borderBottom: `1px solid ${C.bg}`,
  },
  orgRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  expandBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
  },
  expandIcon: {
    fontSize: 9,
    color: C.slate,
    transition: 'transform 0.15s',
    display: 'inline-block',
  },
  orgName: {
    fontSize: 13,
    cursor: 'pointer',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  currentBadge: {
    fontSize: 9,
    fontWeight: 700,
    padding: '1px 6px',
    borderRadius: 4,
    background: `${C.blue}18`,
    color: C.blue,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    flexShrink: 0,
  },
  projectList: {
    paddingLeft: 20,
    paddingBottom: 4,
  },
  projectLoading: {
    padding: '6px 12px',
    fontSize: 11,
    color: C.slate,
    fontStyle: 'italic',
  },
  projectRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    fontSize: 12,
    border: 'none',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    borderRadius: 4,
    transition: 'background 0.1s',
  },
  projectIcon: {
    fontSize: 12,
    flexShrink: 0,
  },
  checkmark: {
    marginLeft: 'auto',
    fontSize: 12,
    color: C.blue,
    fontWeight: 700,
  },
};
